import { computed, inject, Injectable, signal } from '@angular/core';
import { SnackbarNotificationType, SnackbarToastItem } from '../classes/snackbar-notification.model';
import LogService from './log.service';

@Injectable({
    providedIn: 'root'
})
export class SnackbarNotificationService {
    private static readonly DEFAULT_TOAST_DURATION_MS = 4000;

    private readonly logger = inject(LogService);

    private readonly _items = signal<SnackbarToastItem[]>([]);

    /** Current toast queue (read-only for templates). */
    readonly items = this._items.asReadonly();

    readonly count = computed(() => this._items().length);

    private readonly pendingById = new Map<string, { resolve: () => void; reject: (reason?: unknown) => void }>();

    async info(
        message: string,
        durationMs: number = SnackbarNotificationService.DEFAULT_TOAST_DURATION_MS
    ): Promise<void> {
        this.logger.info(message);
        return this.addToast(SnackbarNotificationType.Info, message, durationMs);
    }

    async warn(
        message: string,
        durationMs: number = SnackbarNotificationService.DEFAULT_TOAST_DURATION_MS
    ): Promise<void> {
        this.logger.warn(message);
        return this.addToast(SnackbarNotificationType.Warning, message, durationMs);
    }

    async success(
        message: string,
        durationMs: number = SnackbarNotificationService.DEFAULT_TOAST_DURATION_MS
    ): Promise<void> {
        this.logger.info(message);
        return this.addToast(SnackbarNotificationType.Success, message, durationMs);
    }

    async error(
        message: string,
        durationMs: number = SnackbarNotificationService.DEFAULT_TOAST_DURATION_MS
    ): Promise<void> {
        this.logger.error(message);
        return this.addToast(SnackbarNotificationType.Error, message, durationMs);
    }

    /**
     * Shows a toast with the given type and message. The returned promise resolves when the toast is dismissed (manual close, auto-dismiss, or `dismiss`).
     */
    showToast(
        type: SnackbarNotificationType,
        message: string,
        durationMs: number = SnackbarNotificationService.DEFAULT_TOAST_DURATION_MS
    ): Promise<void> {
        return this.addToast(type, message, durationMs);
    }

    dismiss(id: string): void {
        const pending = this.pendingById.get(id);
        if (pending) {
            pending.resolve();
            this.pendingById.delete(id);
        }
        this._items.update((items) => items.filter((item) => item.id !== id));
    }

    clear(): void {
        for (const [, pending] of this.pendingById) {
            pending.resolve();
        }
        this.pendingById.clear();
        this._items.set([]);
    }

    /**
     * Appends a toast to the queue and returns a promise that settles when it is dismissed.
     */
    private addToast(type: SnackbarNotificationType, message: string, durationMs: number): Promise<void> {
        const id = globalThis.crypto?.randomUUID?.() ?? `snack-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        const item: SnackbarToastItem = { id, type, message, durationMs };

        return new Promise<void>((resolve, reject) => {
            this.pendingById.set(id, { resolve, reject });
            this._items.update((items) => [...items, item]);
        });
    }
}
